import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

/**
 * Initialize PostHog analytics
 * Call once when the app loads (main.tsx)
 */
export const initPostHog = () => {
  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.warn('[PostHog] No API key found. Analytics disabled.');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    respect_dnt: true,
    persistence: 'localStorage+cookie',
    autocapture: true,
    // Mask sensitive data in session recordings for legal/privacy compliance
    mask_all_text: true,
    mask_all_element_attributes: true,
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug();
    },
  });

  if (import.meta.env.DEV) {
    console.log('[PostHog] Initialized');
  }
};

// ─── Identity ────────────────────────────────────────────────

export const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
  if (!POSTHOG_KEY) return;
  posthog.identify(userId, {
    ...properties,
    last_seen: new Date().toISOString(),
  });
};

export const resetUser = () => {
  if (!POSTHOG_KEY) return;
  posthog.reset();
};

export const setUserProperties = (properties: Record<string, unknown>) => {
  if (!POSTHOG_KEY) return;
  posthog.people.set(properties);
};

// ─── Event tracking ──────────────────────────────────────────

export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (!POSTHOG_KEY) return;
  posthog.capture(eventName, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
};

// ─── Feature flags / experiments ─────────────────────────────

export const isFeatureEnabled = (flagKey: string): boolean => {
  if (!POSTHOG_KEY) return false;
  return posthog.isFeatureEnabled(flagKey) ?? false;
};

export const getFeatureFlag = (flagKey: string): string | boolean | undefined => {
  if (!POSTHOG_KEY) return undefined;
  return posthog.getFeatureFlag(flagKey);
};

// ─── Pre-defined advertiser event names ──────────────────────

export const ANALYTICS_EVENTS = {
  // Auth
  SIGN_UP: 'advertiser_signed_up',
  SIGN_IN: 'advertiser_signed_in',
  SIGN_OUT: 'advertiser_signed_out',
  SIGN_IN_FAILED: 'advertiser_sign_in_failed',

  // Campaigns
  CAMPAIGN_CREATED: 'campaign_created',
  CAMPAIGN_UPDATED: 'campaign_updated',
  CAMPAIGN_PAUSED: 'campaign_paused',
  CAMPAIGN_RESUMED: 'campaign_resumed',
  CAMPAIGN_DELETED: 'campaign_deleted',
  CAMPAIGN_VIEWED: 'campaign_viewed',

  // Markets
  MARKET_CREATED: 'market_created',
  MARKET_UPDATED: 'market_updated',
  MARKET_DELETED: 'market_deleted',

  // Results / Leads
  RESULTS_VIEWED: 'results_viewed',
  RESULTS_EXPORTED: 'results_exported',
  RESULTS_FILTERED: 'results_filtered',
  LEAD_DETAIL_VIEWED: 'lead_detail_viewed',

  // Finance
  BILLING_VIEWED: 'billing_viewed',
  REFUND_REQUESTED: 'refund_requested',
  REFUND_APPROVED: 'refund_approved',
  REFUND_REJECTED: 'refund_rejected',

  // Account
  ACCOUNT_SETTINGS_VIEWED: 'account_settings_viewed',
  MFA_ENABLED: 'mfa_enabled',
  MFA_DISABLED: 'mfa_disabled',
  PASSWORD_CHANGED: 'password_changed',

  // Admin
  ADMIN_ACCOUNTS_VIEWED: 'admin_accounts_viewed',
  ADMIN_STATUS_CHANGED: 'admin_advertiser_status_changed',
  ADMIN_REVENUE_VIEWED: 'admin_revenue_viewed',

  // DNC
  DNC_AUDIT_VIEWED: 'dnc_audit_viewed',
  DNC_SEARCH: 'dnc_search',
} as const;

// ─── GTM bridge ──────────────────────────────────────────────

/**
 * Push PostHog distinct_id into GTM dataLayer so GA4 and PostHog
 * can be cross-referenced on the same user.
 */
export const bridgeToGTM = () => {
  if (!POSTHOG_KEY) return;
  const distinctId = posthog.get_distinct_id?.();
  if (distinctId && typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      posthog_distinct_id: distinctId,
    });
  }
};

// Extend window for dataLayer typing
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export default posthog;
